import { useMemo, useState } from "react";
import {
  useCreate,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Building2,
  CalendarCheck2,
  Clock,
  FileText,
  Plus,
  Search,
} from "lucide-react";
import { PageHeader } from "../common/PageHeader";

type PdcRow = {
  id: number;
  reference: string;
  customer_id: number | null;
  invoice_id: number | null;
  cheque_number: string;
  bank_name: string | null;
  drawer_name: string | null;
  cheque_date: string;
  amount: number;
  status: "custody" | "realized" | "bounced" | "cancelled";
  realized_at: string | null;
};

type CompanyOpt = { id: number; name: string };

const fmtINR = (n: number | null | undefined) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const Label = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <label className={`block text-sm font-medium leading-none ${className ?? ""}`}>
    {children}
  </label>
);

export const PDCTrackingPage = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [search, setSearch] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionBusy, setActionBusy] = useState<number | null>(null);
  const [form, setForm] = useState({
    cheque_number: "",
    drawer_name: "",
    bank_name: "",
    customer_id: "",
    cheque_date: "",
    amount: "",
  });

  const { data, isLoading } = useGetList<PdcRow>("pdc_cheques", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "cheque_date", order: "ASC" },
  });

  const { data: companies } = useGetList<CompanyOpt>("companies", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });

  const rows = useMemo(
    () =>
      (data ?? []).filter((row) =>
        search
          ? [row.cheque_number, row.drawer_name, row.bank_name, row.reference]
              .filter(Boolean)
              .some((field) =>
                (field as string).toLowerCase().includes(search.toLowerCase()),
              )
          : true,
      ),
    [data, search],
  );

  const totalFace = useMemo(
    () =>
      rows
        .filter((r) => r.status === "custody")
        .reduce((acc, r) => acc + Number(r.amount ?? 0), 0),
    [rows],
  );

  const handleRegister = async () => {
    if (!form.cheque_number || !form.amount || !form.cheque_date) {
      notify("Cheque number, amount and date are required.", { type: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      await create(
        "pdc_cheques",
        {
          data: {
            cheque_number: form.cheque_number,
            drawer_name: form.drawer_name || null,
            bank_name: form.bank_name || null,
            customer_id: form.customer_id ? Number(form.customer_id) : null,
            cheque_date: form.cheque_date,
            amount: Number(form.amount),
            status: "custody",
          },
        },
        { returnPromise: true },
      );
      notify("PDC registered.", { type: "success" });
      setSheetOpen(false);
      setForm({
        cheque_number: "",
        drawer_name: "",
        bank_name: "",
        customer_id: "",
        cheque_date: "",
        amount: "",
      });
      refresh();
    } catch (error) {
      notify(`Failed: ${(error as Error).message}`, { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (
    row: PdcRow,
    status: "realized" | "bounced" | "cancelled",
  ) => {
    setActionBusy(row.id);
    try {
      await update(
        "pdc_cheques",
        {
          id: row.id,
          data: {
            status,
            realized_at: status === "realized" ? new Date().toISOString() : null,
          },
          previousData: row,
        },
        { returnPromise: true },
      );
      notify(`PDC marked ${status}.`, { type: "success" });
      refresh();
    } catch (error) {
      notify(`Failed: ${(error as Error).message}`, { type: "error" });
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div className="p-6 bg-slate-50/30 min-h-screen">
      <PageHeader
        title="PDC Tracking Ledger"
        description="Post-dated cheques in custody. Realized automatically when their date arrives."
      />

      <div className="flex gap-4 mb-6 overflow-x-auto pb-2">
        <div className="min-w-[220px] flex-1 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-slate-500 text-[10px] font-extrabold uppercase tracking-wider">
            Active In Custody
          </p>
          <h3 className="text-3xl font-black mt-1 text-slate-900">
            {rows.filter((r) => r.status === "custody").length}{" "}
            <span className="text-sm text-slate-400 font-bold">Instruments</span>
          </h3>
        </div>
        <div className="min-w-[220px] flex-1 bg-white p-5 rounded-xl border-b-4 border-b-emerald-500 shadow-sm">
          <p className="text-slate-500 text-[10px] font-extrabold uppercase tracking-wider">
            Total Face Reserve
          </p>
          <h3 className="text-3xl font-black mt-1 text-emerald-600">
            {fmtINR(totalFace)}
          </h3>
        </div>
      </div>

      <Card className="shadow-none border border-slate-200 bg-white overflow-hidden">
        <CardHeader className="border-b bg-slate-50/50 flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base font-bold flex items-center gap-2 text-slate-900">
            <CalendarCheck2 className="h-5 w-5 text-slate-500" /> Maturing Instrument
            Registry
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative max-w-xs hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search instruments..."
                className="h-8 pl-8 border-slate-300 text-xs shadow-none w-[180px] bg-white"
              />
            </div>
            <Button
              onClick={() => setSheetOpen(true)}
              className="bg-slate-900 hover:bg-slate-800 font-bold text-white shadow-md text-xs h-8 gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" /> Register PDC
            </Button>
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Cheque No.
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Drawer
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Drawee Bank
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Cheque Date
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200 text-right">
                  Value
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 text-right">
                  Status / Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    Loading PDC ledger…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    No PDC instruments yet.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const isBusy = actionBusy === row.id;
                return (
                  <tr
                    key={row.id}
                    className={`${row.status !== "custody" ? "opacity-70 bg-slate-50" : "even:bg-slate-50/50 hover:bg-slate-100/50"} border-b border-slate-200 transition-all duration-300`}
                  >
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-mono font-bold text-slate-800 align-middle">
                      {row.cheque_number}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-bold text-slate-900 align-middle">
                      {row.drawer_name ?? "—"}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-slate-500 align-middle">
                      <span className="flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 opacity-60" />
                        {row.bank_name ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-slate-500 align-middle">
                      {fmtDate(row.cheque_date)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-right font-black text-slate-900 align-middle">
                      {fmtINR(row.amount)}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      {row.status === "realized" && (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 font-bold shadow-none">
                          ✓ Realized
                        </Badge>
                      )}
                      {row.status === "bounced" && (
                        <Badge className="bg-rose-100 text-rose-800 border-rose-200 font-bold shadow-none">
                          Bounced
                        </Badge>
                      )}
                      {row.status === "cancelled" && (
                        <Badge className="bg-slate-200 text-slate-700 border-slate-300 font-bold shadow-none">
                          Cancelled
                        </Badge>
                      )}
                      {row.status === "custody" && (
                        <div className="flex gap-1.5 justify-end">
                          <Button
                            disabled={isBusy}
                            onClick={() => setStatus(row, "bounced")}
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs font-bold border-rose-200 text-rose-700 hover:bg-rose-50"
                          >
                            Bounce
                          </Button>
                          <Button
                            disabled={isBusy}
                            onClick={() => setStatus(row, "cancelled")}
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs font-bold border-slate-200 text-slate-600 hover:bg-slate-50"
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-[440px] p-0 flex flex-col bg-white border-l border-slate-200 h-full">
          <SheetHeader className="p-6 bg-slate-900 text-white">
            <SheetTitle className="text-white text-xl font-bold flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-400" /> Register Physical PDC
            </SheetTitle>
            <SheetDescription className="text-slate-400 font-medium mt-1">
              Insert incoming post-dated instrument into safe custody.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="space-y-1.5">
              <Label className="text-slate-600 font-bold text-sm">Customer</Label>
              <select
                value={form.customer_id}
                onChange={(e) =>
                  setForm({ ...form, customer_id: e.target.value })
                }
                className="w-full h-11 px-3 border border-slate-300 bg-white rounded-md font-bold text-slate-900"
              >
                <option value="">— select —</option>
                {(companies ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-600 font-bold text-sm">
                Drawer Name
              </Label>
              <Input
                value={form.drawer_name}
                onChange={(e) =>
                  setForm({ ...form, drawer_name: e.target.value })
                }
                placeholder="e.g. Reliance Industries Ltd."
                className="h-11 border-slate-300 font-bold"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-600 font-bold text-sm">
                  Cheque No.
                </Label>
                <Input
                  value={form.cheque_number}
                  onChange={(e) =>
                    setForm({ ...form, cheque_number: e.target.value })
                  }
                  placeholder="123456"
                  className="h-11 border-slate-300 font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-600 font-bold text-sm">Amount</Label>
                <Input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className="h-11 border-slate-300 font-bold"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-600 font-bold text-sm">
                  Drawee Bank
                </Label>
                <Input
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  placeholder="ICICI / HDFC / etc."
                  className="h-11 border-slate-300"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-600 font-bold text-sm">
                  Cheque Date
                </Label>
                <Input
                  type="date"
                  value={form.cheque_date}
                  onChange={(e) =>
                    setForm({ ...form, cheque_date: e.target.value })
                  }
                  className="h-11 border-slate-300"
                />
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs font-bold flex items-center gap-2.5 text-slate-600">
              <Clock className="h-4 w-4 text-primary" />
              <span>
                System auto-realises cheques on or after the maturity date (daily
                02:00 IST).
              </span>
            </div>
          </div>

          <SheetFooter className="p-6 bg-slate-50 border-t">
            <Button
              onClick={handleRegister}
              disabled={submitting}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg text-lg gap-2"
            >
              {submitting ? "Saving…" : "Commit to Ledger"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};
